import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * related-products — "You may also like". A grid of {@link ProductCard}s. Source = hand-picked products
 * via repeatable `product` BLOCKS (each a product-picker); if none are picked, the section falls back to
 * the first `fallbackCount` demo products so the builder preview always renders. The heading uses the
 * shared word-HIGHLIGHT engines (1+2) + size + alignment, and an optional 13-pattern background can sit
 * behind it — the same engines the rest of the theme shares. Card options mirror the shared ProductCard
 * option set (imageRatio / showRating / quickAdd). Layout controls the column count; the shared "Section"
 * universal group controls scheme / padding / width.
 */
export const relatedProductsSchema = {
    type: 'related-products',
    name: 'Related products',
    version: 1,
    templates: ['product', 'collection', 'index'],
    fields: [
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'Content',
            default: { format: 'html', html: '<p>Vous aimerez aussi</p>' },
            info: 'Bold a word to apply highlight 1, italic to apply highlight 2.',
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h2',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
            ],
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: 'Content',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        ...headingHighlightFields(1).map((f) => f.id === 'headingHighlight1'
            ? { ...f, info: 'Bold a word in the heading to style it with this.' }
            : f),
        ...headingHighlightFields(2).map((f) => f.id === 'headingHighlight2'
            ? { ...f, info: 'Italicize a word in the heading to style it with this.' }
            : f),
        // ── Source ──────────────────────────────────────────────────────────────
        {
            id: 'fallbackCount',
            type: 'number',
            label: 'Auto products',
            group: 'Source',
            info: 'When no products are picked below, show this many automatically (demo in the builder).',
            default: 4,
            min: 1,
            max: 12,
            step: 1,
        },
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'columns',
            type: 'select',
            label: 'Columns',
            group: 'Layout',
            default: '4',
            options: [
                { value: '2', label: '2 columns' },
                { value: '3', label: '3 columns' },
                { value: '4', label: '4 columns' },
            ],
        },
        // ── Card ────────────────────────────────────────────────────────────────
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Card',
            default: 'portrait',
            options: [
                { value: 'square', label: 'Square (1:1)' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'landscape', label: 'Landscape (3:2)' },
            ],
        },
        {
            id: 'showRating',
            type: 'checkbox',
            label: 'Show rating stars',
            group: 'Card',
            default: true,
        },
        {
            id: 'quickAdd',
            type: 'checkbox',
            label: 'Quick-add button',
            group: 'Card',
            default: false,
        },
        // ── Background pattern (shared engine). ──────────────────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Section (universal) ──────────────────────────────────────────────────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [
        {
            type: 'product',
            name: 'Product',
            max: 12,
            fields: [{ id: 'ref', type: 'product-picker', label: 'Product' }],
        },
    ],
    maxBlocks: 12,
};
export const relatedProductsPresets = [
    {
        name: 'Related products',
        settings: {
            heading: '@t:section.you_may_also_like',
            fallbackCount: 4,
            columns: '4',
            imageRatio: 'portrait',
            showRating: true,
            quickAdd: false,
        },
        // No picked products by default → the section renders the demo grid in the builder preview.
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const relatedProductsMeta = {
    label: 'Related products',
    group: 'Product',
    icon: 'gift',
};
//# sourceMappingURL=schema.js.map